import React, { useState, useEffect } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ChevronDown, ChevronRight, Users, UserCheck, UserX, Clock, User } from 'lucide-react';
import { format } from 'date-fns';
import { useFamilyMemberCheckins, FamilyMemberCheckin } from '@/hooks/useFamilyMemberCheckins';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';

interface FamilyMemberRowProps {
  client: {
    id: string;
    name: string;
    age?: number;
    room?: string;
    status: string;
    caseWorker?: string;
    site?: string;
    isHeadOfHousehold?: boolean;
    partnerName?: string;
    children?: { name: string; age?: number }[];
    passStart?: string;
    passEnd?: string;
    photo_url?: string;
  };
  siteId: string;
  onSelectClient?: (clientId: string) => void;
  getClientPhoto?: (clientId: string) => string;
  onNotPresent?: (clientId: string) => void;
}

const FamilyMemberRow: React.FC<FamilyMemberRowProps> = ({ client, siteId, onSelectClient, getClientPhoto, onNotPresent }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { user } = useAuth();
  const { 
    memberCheckins, 
    toggleMemberStatus, 
    initializeFamilyMembers,
    getFamilyStatus,
  } = useFamilyMemberCheckins(client.id, siteId);

  const hasFamily = client.partnerName || (client.children && client.children.length > 0);
  const totalFamilyMembers = (client.partnerName ? 1 : 0) + (client.children?.length || 0);

  useEffect(() => {
    if (hasFamily && memberCheckins.length === 0 && siteId) {
      initializeFamilyMembers.mutate({
        clientId: client.id,
        siteId,
        partnerName: client.partnerName,
        children: client.children,
      });
    }
  }, [hasFamily, memberCheckins.length, siteId, client.id]);

  const familyStatus = getFamilyStatus(memberCheckins);
  const checkedInCount = memberCheckins.filter(m => m.status === 'checked-in').length;

  const handleToggleMember = (member: FamilyMemberCheckin) => {
    if (!user?.id) return;
    const newStatus = member.status === 'checked-in' ? 'checked-out' : 'checked-in';
    toggleMemberStatus.mutate({
      memberId: member.id,
      newStatus,
      userId: user.id,
      memberName: member.member_name,
      memberType: member.member_type,
      clientId: client.id,
      siteId,
    });
  };

  const getInitials = (name: string) => name.split(' ').map(n => n[0]).join('').slice(0, 2);

  // Check if client has an active pass
  const isOnPass = (() => {
    if (client.passStart && client.passEnd) {
      const now = new Date();
      return now >= new Date(client.passStart) && now <= new Date(client.passEnd);
    }
    return false;
  })();

  const [resolvedPhotoUrl, setResolvedPhotoUrl] = useState<string | null>(null);
  
  useEffect(() => {
    if (client.photo_url) {
      import('@/utils/storageUtils').then(({ resolvePhotoUrl }) => {
        resolvePhotoUrl(client.photo_url).then(url => {
          if (url) setResolvedPhotoUrl(url);
        });
      });
    }
  }, [client.photo_url]);

  const photoUrl = resolvedPhotoUrl || (getClientPhoto ? getClientPhoto(client.id) : '') || undefined;

  const getStatusBadge = () => {
    if (!hasFamily) {
      if (isOnPass) {
        return (
          <Badge className="text-xs font-medium bg-[hsl(var(--status-pass))] text-[hsl(var(--status-pass-foreground))]">
            Pass
          </Badge>
        );
      }
      return (
        <div className="flex items-center gap-2">
          <Badge
            className={cn(
              'text-xs font-medium',
              client.status === 'checked-in'
                ? 'bg-[hsl(var(--status-checked-in))] text-[hsl(var(--status-checked-in-foreground))]'
                : client.status === 'not_present'
                ? 'bg-[hsl(var(--status-not-present))] text-[hsl(var(--status-not-present-foreground))]'
                : 'bg-[hsl(var(--status-checked-out))] text-[hsl(var(--status-checked-out-foreground))]'
            )}
          >
            {client.status === 'checked-in' ? 'In' : client.status === 'not_present' ? 'Not Present' : 'Out'}
          </Badge>
          {client.status === 'checked-out' && onNotPresent && (
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs text-[hsl(var(--status-not-present))] border-[hsl(var(--status-not-present))]/30 hover:bg-[hsl(var(--status-not-present))]/10"
              onClick={(e) => {
                e.stopPropagation();
                onNotPresent(client.id);
              }}
            >
              Not Present
            </Button>
          )}
        </div>
      );
    }

    if (familyStatus === 'all-in') {
      return (
        <Badge className="text-xs bg-[hsl(var(--status-checked-in))] text-[hsl(var(--status-checked-in-foreground))]">
          <Users className="h-3 w-3 mr-1" />
          {totalFamilyMembers}/{totalFamilyMembers}
        </Badge>
      );
    }

    if (familyStatus === 'all-out') {
      return (
        <Badge className="text-xs bg-[hsl(var(--status-checked-out))] text-[hsl(var(--status-checked-out-foreground))]">
          <Users className="h-3 w-3 mr-1" />
          0/{totalFamilyMembers}
        </Badge>
      );
    }

    return (
      <Badge className="text-xs bg-amber-500/10 text-amber-700 border-amber-200">
        <Users className="h-3 w-3 mr-1" />
        {checkedInCount}/{totalFamilyMembers}
      </Badge>
    );
  };

  const getMemberStatusBadge = (member: FamilyMemberCheckin) => {
    const isIn = member.status === 'checked-in';
    const time = isIn ? member.check_in_time : member.check_out_time;
    
    return (
      <div className="flex items-center gap-1.5">
        <Badge 
          className={cn(
            'cursor-pointer text-xs px-2 py-0.5',
            isIn 
              ? 'bg-[hsl(var(--status-checked-in))] text-[hsl(var(--status-checked-in-foreground))] hover:opacity-80' 
              : 'bg-[hsl(var(--status-checked-out))] text-[hsl(var(--status-checked-out-foreground))] hover:opacity-80'
          )}
          onClick={(e) => {
            e.stopPropagation();
            handleToggleMember(member);
          }}
        >
          {isIn ? <UserCheck className="h-3 w-3" /> : <UserX className="h-3 w-3" />}
        </Badge>
        {time && (
          <span className="text-xs text-muted-foreground flex items-center gap-0.5">
            <Clock className="h-3 w-3" />
            {format(new Date(time), 'h:mm a')}
          </span>
        )}
      </div>
    );
  };

  // Regular row for non-family clients
  if (!hasFamily) {
    return (
      <tr className="border-b hover:bg-muted/50">
        <td className="p-4 w-[35%]">
          <div 
            className="flex items-center gap-3 cursor-pointer hover:text-primary"
            onClick={() => onSelectClient?.(client.id)}
          >
            <Avatar className="h-10 w-10">
              {photoUrl && <AvatarImage src={photoUrl} alt={client.name} />}
              <AvatarFallback className="bg-muted">
                <User className="h-1/2 w-1/2 text-muted-foreground" />
              </AvatarFallback>
            </Avatar>
            <span className="text-sm font-medium text-primary hover:underline underline-offset-2 cursor-pointer">{client.name}</span>
          </div>
        </td>
        <td className="p-4 w-[12%]">
          <span className="font-mono text-sm">{client.room || 'N/A'}</span>
        </td>
        <td className="p-4 w-[10%]">
          <span className="text-sm text-muted-foreground">{client.age || ''}</span>
        </td>
        <td className="p-4 w-[20%]">
          <span className="text-sm text-muted-foreground">{client.caseWorker || '—'}</span>
        </td>
        <td className="p-4 w-[23%]">{getStatusBadge()}</td>
      </tr>
    );
  }

  // Expandable row for family clients
  return (
    <Collapsible open={isExpanded} onOpenChange={setIsExpanded} asChild>
      <>
        <tr className="border-b hover:bg-muted/50 cursor-pointer" onClick={() => setIsExpanded(!isExpanded)}>
          <td className="p-4 w-[35%]">
            <div className="flex items-center gap-3">
              <CollapsibleTrigger asChild onClick={(e) => e.stopPropagation()}>
                <Button variant="ghost" size="icon" className="h-6 w-6 p-0 shrink-0">
                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                </Button>
              </CollapsibleTrigger>
              <div 
                className="flex items-center gap-3 cursor-pointer hover:text-primary"
                onClick={(e) => {
                  e.stopPropagation();
                  onSelectClient?.(client.id);
                }}
              >
                <Avatar className="h-10 w-10">
                  {photoUrl && <AvatarImage src={photoUrl} alt={client.name} />}
                  <AvatarFallback className="bg-muted">
                    <User className="h-1/2 w-1/2 text-muted-foreground" />
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm font-medium text-primary hover:underline underline-offset-2 cursor-pointer">{client.name}</span>
              </div>
              <Badge variant="outline" className="text-[10px] px-1.5 py-0 bg-blue-50 text-blue-700 border-blue-200">
                Family
              </Badge>
            </div>
          </td>
          <td className="p-4 w-[12%]">
            <span className="font-mono text-sm">{client.room || 'N/A'}</span>
          </td>
          <td className="p-4 w-[10%]">
            <span className="text-sm text-muted-foreground">{client.age || ''}</span>
          </td>
          <td className="p-4 w-[20%]">
            <span className="text-sm text-muted-foreground">{client.caseWorker || '—'}</span>
          </td>
          <td className="p-4 w-[23%]">{getStatusBadge()}</td>
        </tr>
        
        <CollapsibleContent asChild>
          <>
            {/* Partner row */}
            {client.partnerName && (() => {
              const partnerRecord = memberCheckins.find(m => m.member_type === 'partner');
              return (
                <tr className="bg-muted/30 border-b">
                  <td className="p-4 pl-16 w-[35%]">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-muted">
                          <User className="h-1/2 w-1/2 text-muted-foreground" />
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{client.partnerName}</span>
                      <span className="text-xs text-muted-foreground">Partner</span>
                    </div>
                  </td>
                  <td className="p-4 w-[12%]">
                    <span className="text-sm text-muted-foreground">—</span>
                  </td>
                  <td className="p-4 w-[10%]">
                    <span className="text-sm text-muted-foreground">Adult</span>
                  </td>
                  <td className="p-4 w-[20%]">
                    <span className="text-sm text-muted-foreground">—</span>
                  </td>
                  <td className="p-4 w-[23%]">
                    {partnerRecord && getMemberStatusBadge(partnerRecord)}
                  </td>
                </tr>
              );
            })()}
            
            {/* Children rows */}
            {client.children?.map((child, index) => {
              const memberRecord = memberCheckins.find(
                m => m.member_type === 'child' && m.member_name === child.name
              );
              return (
                <tr key={index} className="bg-muted/30 border-b">
                  <td className="p-4 pl-16 w-[35%]">
                    <div className="flex items-center gap-3">
                      <Avatar className="h-8 w-8">
                        <AvatarFallback className="bg-muted">
                          <User className="h-1/2 w-1/2 text-muted-foreground" />
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-sm">{child.name}</span>
                      <span className="text-xs text-muted-foreground">Child</span>
                    </div>
                  </td>
                  <td className="p-4 w-[12%]">
                    <span className="text-sm text-muted-foreground">—</span>
                  </td>
                  <td className="p-4 w-[10%]">
                    <span className="text-sm text-muted-foreground">{child.age || ''}</span>
                  </td>
                  <td className="p-4 w-[20%]">
                    <span className="text-sm text-muted-foreground">—</span>
                  </td>
                  <td className="p-4 w-[23%]">
                    {memberRecord && getMemberStatusBadge(memberRecord)}
                  </td>
                </tr>
              );
            })}
          </>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
};

export default FamilyMemberRow;
